import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Seo, Eyebrow, CTAButton, ArrowLink, Stat } from "@/components/site/primitives";
import { Reveal, Stagger, Item } from "@/components/site/motion";
import { PageHero, Section, Pill } from "@/components/site/pageparts";
import { CopyInstall, VersionBadge, PypiStats } from "@/components/site/CopyInstall";
import { ComparisonTable } from "@/components/site/ComparisonTable";
import { MemoryDemo } from "@/components/site/MemoryDemo";
import { IngestionDiagram } from "@/components/site/Diagrams";
import { SOLUTIONS } from "@/data/site";

const WHY = [
  { t: "A tool", d: "AI without memory is a tool — powerful, but it forgets you the moment the session ends.", tone: "text-mist" },
  { t: "A trap", d: "AI with rented memory is a trap — your context is uploaded, metered, and held by a vendor.", tone: "text-mist" },
  { t: "A partner", d: "AI with sovereign memory is a partner — it remembers, cites its sources, and answers to you.", tone: "text-saffron" },
];

const CURVES = [
  { n: "01", t: "Agents are arriving", d: "Autonomous agents need durable, owned memory to act on your behalf — not a context window that resets." },
  { n: "02", t: "Context costs are exploding", d: "Paying per token to re-send your own history back to a model does not scale. Local memory is flat." },
  { n: "03", t: "DPDP / GDPR enforcement is tightening", d: "Regulators want residency, provenance and audit. Cloud memory struggles to provide any of the three." },
  { n: "04", t: "Local hardware is finally fast enough", d: "On-device embeddings and reasoning are now practical. The four curves cross in 2026." },
];

export default function Smritium() {
  return (
    <div data-testid="smritium-page">
      <Seo title="Smritium — the sovereign memory layer for AI" description="Your memory. Sovereign. Verifiable. Local. Smritium is the sovereign memory layer for AI." />
      <PageHero
        eyebrow="Flagship · Smritium"
        title={<>Your memory. Sovereign, verifiable, local.</>}
        lead="Smritium is the sovereign memory layer for AI — a local-first substrate that gives every model deep, private context without surrendering a single byte to the cloud."
      >
        <CTAButton href="https://pypi.org/project/upii/" external variant="solid">
          Install the core <ArrowRight className="h-4 w-4" />
        </CTAButton>
        <CTAButton to="/smritium/how-it-works" variant="outline">
          How it works
        </CTAButton>
      </PageHero>

      {/* Live strip */}
      <div className="border-b border-white/10 bg-ink">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-4 px-5 py-4 md:px-10">
          <span className="text-sm text-bone">Open-source core live on PyPI — upii v0.7.0, early preview, core stable.</span>
          <div className="flex items-center gap-6">
            <VersionBadge />
            <Link to="/smritium/changelog" className="link-underline pb-0.5 text-sm text-bone hover:text-saffron">
              Changelog
            </Link>
          </div>
        </div>
      </div>

      {/* Why it matters */}
      <Section>
        <Reveal>
          <Eyebrow>Why it matters</Eyebrow>
          <h2 className="mt-5 max-w-3xl text-balance font-serif text-3xl leading-[1.06] tracking-tighter text-bone md:text-5xl">
            The difference between a tool, a trap, and a partner is memory.
          </h2>
        </Reveal>
        <Stagger className="mt-16 grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
          {WHY.map((w) => (
            <Item key={w.t} className="bg-ink p-8 md:p-10">
              <div className={`font-serif text-2xl ${w.tone}`}>{w.t}</div>
              <p className="mt-4 text-[15px] leading-relaxed text-mist">{w.d}</p>
            </Item>
          ))}
        </Stagger>
      </Section>

      {/* Why now */}
      <Section className="border-t border-white/10">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <Eyebrow>Why now</Eyebrow>
            <h2 className="mt-5 font-serif text-3xl leading-[1.06] tracking-tighter text-bone md:text-5xl">
              Four curves cross in 2026.
            </h2>
            <p className="mt-6 max-w-sm text-[15px] leading-relaxed text-mist">
              Sovereign memory is not early and not late. It is exactly on time.
            </p>
          </Reveal>
          <Stagger className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:col-span-8">
            {CURVES.map((c) => (
              <Item key={c.n} className="bg-ink p-7">
                <div className="font-mono text-sm text-saffron">{c.n}</div>
                <h3 className="mt-4 font-serif text-xl text-bone">{c.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mist">{c.d}</p>
              </Item>
            ))}
          </Stagger>
        </div>
      </Section>

      {/* How it stands apart */}
      <Section className="border-t border-white/10">
        <Reveal>
          <Eyebrow>How it stands apart</Eyebrow>
          <h2 className="mt-5 max-w-3xl text-balance font-serif text-3xl leading-[1.06] tracking-tighter text-bone md:text-5xl">
            Most memory for AI is a cloud you rent. Smritium is a substrate you own.
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="mt-14">
          <ComparisonTable />
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-14 grid grid-cols-2 gap-8 border-y border-white/10 py-12 md:grid-cols-4">
            <Stat value="0.958" label="Recall@10 on a committed labelled set" />
            <Stat value="100%" label="chunk-hash reproducibility on re-ingest" />
            <Stat value="0" label="required cloud calls to answer" />
            <Stat value="3" label="retrieval signals fused per query" />
          </div>
          <p className="mt-6 max-w-measure text-sm text-mist">
            Verify, don't trust — the evaluation harness ships with the open-source core.
          </p>
        </Reveal>
      </Section>

      {/* Live today */}
      <Section className="border-t border-white/10">
        <div className="grid gap-12 lg:grid-cols-2">
          <Reveal>
            <Pill tone="signal">Live today</Pill>
            <h2 className="mt-6 font-serif text-3xl leading-tight tracking-tighter text-bone md:text-4xl">
              The core is running. Install it and query cited memory in minutes.
            </h2>
            <p className="mt-6 max-w-md text-[15px] leading-relaxed text-mist">
              v0.7.x is an early preview with a stable core: content-addressed ingestion, the Context Rehydrator,
              a local knowledge graph, and a 12-command CLI.
            </p>
            <div className="mt-8">
              <ArrowLink to="/smritium/changelog">Read the changelog</ArrowLink>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border border-white/10 bg-white/[0.015] p-8">
              <IngestionDiagram className="text-bone/70" />
            </div>
            <div className="mt-6">
              <CopyInstall variant="block" />
              <PypiStats className="mt-4" />
            </div>
          </Reveal>
        </div>
      </Section>

      {/* Try it — interactive cite-or-abstain demo */}
      <Section className="border-t border-white/10">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <Pill tone="saffron">Try it</Pill>
            <h2 className="mt-6 font-serif text-3xl leading-tight tracking-tighter text-bone md:text-4xl">
              Ask a sample corpus. Watch it cite — or abstain.
            </h2>
            <p className="mt-6 max-w-md text-[15px] leading-relaxed text-mist">
              A tiny, offline demo of the real behaviour. Every answer resolves to a source chunk; when the corpus has
              no support, it refuses rather than inventing one.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <MemoryDemo />
          </Reveal>
        </div>
      </Section>

      {/* Use-case grid */}
      <Section className="border-t border-white/10">
        <Reveal>
          <Eyebrow>Built for</Eyebrow>
          <h2 className="mt-5 font-serif text-3xl tracking-tighter text-bone md:text-5xl">One substrate, every sector.</h2>
        </Reveal>
        <Stagger className="mt-14 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {SOLUTIONS.map((s) => (
            <Item key={s.slug}>
              <Link to={`/solutions/${s.slug}`} className="group flex h-full flex-col bg-ink p-7 transition-colors duration-300 hover:bg-white/[0.03]">
                <div className="flex items-center justify-between">
                  <span className="font-serif text-lg text-bone group-hover:text-saffron">{s.name}</span>
                  <ArrowUpRight className="h-4 w-4 text-mist opacity-0 transition-opacity group-hover:opacity-100" />
                </div>
                <p className="mt-3 text-sm leading-relaxed text-mist">{s.tagline}</p>
              </Link>
            </Item>
          ))}
        </Stagger>
      </Section>

      {/* CTA */}
      <Section className="border-t border-white/10">
        <Reveal className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <h2 className="max-w-2xl text-balance font-serif text-3xl leading-tight tracking-tighter text-bone md:text-5xl">
            Run the sovereign memory layer.
          </h2>
          <div className="flex flex-wrap gap-4">
            <CTAButton href="https://pypi.org/project/upii/" external variant="solid">
              View on PyPI <ArrowUpRight className="h-4 w-4" />
            </CTAButton>
            <CTAButton to="/company/contact" variant="outline">
              Talk to us
            </CTAButton>
          </div>
        </Reveal>
        <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 font-mono text-xs text-mist">
          <Link to="/legal/terms" className="hover:text-bone">Terms</Link>
          <Link to="/legal/privacy" className="hover:text-bone">Privacy</Link>
          <Link to="/legal/acceptable-use" className="hover:text-bone">Acceptable Use</Link>
          <Link to="/smritium/security" className="hover:text-bone">Security</Link>
        </div>
      </Section>
    </div>
  );
}
